import React, { Fragment, useMemo } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";

import { Grid, Column } from "../../../../components/src";
import { numberFormatFun } from "../../../../common/src/utils/FormatFunctions";

const getParties = gql`
    query getParties {
        parties(filter: { typeIn: [Fund], statusIn: [Confirmed] }) {
            _id
            name
        }
    }
`;

const getData = gql`
    query getData($clientIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString) {
        tradingmanager(filterZeroPositions: true, filter: { endDate: $endDate, clientIds: $clientIds }, lookThrough: true) {
            clientId
            currency
            localExposure
            modelType
        }
    }
`;

type CurrencyExposureRow = {
    clientId: string;
    clientName: string;
    currency: string;
    amount: number;
    div1000: number;
};

export function DailyEod({ endDate }: { endDate: string }): React.ReactElement {
    const [{ fetching: loadingParties, data: dataParties }] = useQuery({ query: getParties, requestPolicy: "cache-and-network" });

    const clientDict = useMemo(() => {
        const dict: Record<string, string> = {};
        if (dataParties?.parties) {
            dataParties.parties.forEach(({ _id, name }) => {
                dict[_id] = name;
            });
        }
        return dict;
    }, [dataParties]);

    const [{ fetching: loading, error: error, data: data }] = useQuery({
        query: getData,
        pause: !clientDict,
        variables: { clientIds: Object.keys(clientDict), endDate: endDate },
        requestPolicy: "cache-and-network"
    });

    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    if (loadingParties || loading || !data) return <p>Loading...</p>;

    const cashSekTable: CurrencyExposureRow[] = [];
    const cashOtherTable: CurrencyExposureRow[] = [];
    Object.keys(clientDict).forEach((clientId) => {
        const uniqueCurrencies = [
            ...new Set(data.tradingmanager.filter((item) => item.clientId === clientId).map((item) => item.currency))
        ];
        uniqueCurrencies.forEach((currency) => {
            const totalCcyExposure = data.tradingmanager
                .filter((item) => item.modelType == "Balance" && item.clientId === clientId && item.currency === currency)
                .reduce((sum, item) => sum + (item.localExposure ?? 0), 0);
            if (currency === "SEK") {
                cashSekTable.push({
                    clientId: clientId,
                    clientName: clientDict[clientId],
                    currency: String(currency ?? ""),
                    amount: totalCcyExposure,
                    div1000: totalCcyExposure / 1000
                });
            } else {
                cashOtherTable.push({
                    clientId: clientId,
                    clientName: clientDict[clientId],
                    currency: String(currency ?? ""),
                    amount: totalCcyExposure,
                    div1000: totalCcyExposure / 1000
                });
            }
        });
    });

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 md:w-1/2">
                    <Grid header="Cash balances Other" data={cashOtherTable} sortable tableClassName="table-xs">
                        <Column
                            field="clientName"
                            title="Client"
                            format={(_, item) => {
                                return <Link to={"/parties/" + item.clientId}>{item.clientName}</Link>;
                            }}
                        />
                        <Column field="div1000" format={numberFormatFun("###0.000")} />
                        <Column field="currency" />
                        <Column field="amount" format={numberFormatFun("# ##0")} />
                    </Grid>
                </div>
                <div className="w-full px-2 md:w-1/2">
                    <Grid header="Cash balances SEK" data={cashSekTable} sortable tableClassName="table-xs">
                        <Column
                            field="clientName"
                            title="Client"
                            format={(_, item) => {
                                return <Link to={"/parties/" + item.clientId}>{item.clientName}</Link>;
                            }}
                        />
                        <Column field="div1000" format={numberFormatFun("###0.000")} />
                        <Column field="currency" />
                        <Column field="amount" format={numberFormatFun("# ##0")} />
                    </Grid>
                </div>
            </div>
        </Fragment>
    );
}
